import {
  Box,
  FormErrorMessage,
  FormControl,
  Select,
  VStack,
  FormLabel,
  SimpleGrid,
  Checkbox,
  Text,
} from '@chakra-ui/react';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import CustomInput from '@/components/common/Input';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Asterisk from '@/components/common/Asterisk';
import { useGetUserRoleTitles } from '@/services/userManagement';
import { IRoles, RoleTitle, User } from '@/services/userManagement/types';

export interface IAddUserData {
  id?: number;
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  role: { role_id: string }[];
}

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required.'),
  last_name: Yup.string().required('Last name is required.'),
  email: Yup.string().email('Invalid email format').required('Email is required.'),
  title: Yup.string().required('Title is required.'),
  role: Yup.array()
    .min(1, 'At least one role must be selected.')
    .required('At least one role must be selected.'),
});

interface AddUserFormProps {
  onSubmit: (data: IAddUserData) => void;
  isSuccess: boolean;
  roles: IRoles[];
  editUserData?: User | null;
}

const AddUserForm = forwardRef(
  ({ onSubmit, isSuccess, roles, editUserData }: AddUserFormProps, ref: React.Ref<() => void>) => {
    const { data: roleTitles } = useGetUserRoleTitles();

    const {
      control,
      handleSubmit,
      reset,
      formState: { errors },
      setValue,
      watch,
    } = useForm<IAddUserData>({
      resolver: yupResolver(validationSchema),
      defaultValues: {
        first_name: '',
        last_name: '',
        email: '',
        title: '',
        role: [],
      },
    });

    const selectedRoles = watch('role');
    const selectedTitle = watch('title'); // Watch the title field
    const isEditMode = !!editUserData;
    const prevTitleRef = useRef<string | null>(null); // Track previous title
    const prevAutoCheckedRoleRef = useRef<string | null>(null); // Track previously auto-checked role

    useEffect(() => {
      if (editUserData) {
        const userRoles = editUserData.roles.map((r) => ({ role_id: String(r.id) })) || [];
        reset({
          first_name: editUserData.first_name || '',
          last_name: editUserData.last_name || '',
          email: editUserData.email || '',
          title:
            roleTitles?.find((role) => role.name === editUserData.assigned_title)?.id?.toString() ||
            '',
          role: userRoles,
        });
      }
    }, [editUserData, reset, roleTitles]);

    // Auto-check the role that matches the selected title
    useEffect(() => {
      if (selectedTitle && roleTitles && roles && selectedTitle !== prevTitleRef.current) {
        const selectedTitleName =
          roleTitles.find((title) => title.id.toString() === selectedTitle)?.name || '';
        const matchingRole = roles.find(
          (role) => role.name.toLowerCase() === selectedTitleName.toLowerCase(),
        );
        const adminRole = roles.find((role) => role.name === 'Admin');
        const isAdminTitle = selectedTitleName.toLowerCase() === 'admin';

        if (isAdminTitle && adminRole) {
          // If Admin title is selected, check all roles
          setValue(
            'role',
            roles.map((r) => ({ role_id: String(r.id) })),
          );
          prevAutoCheckedRoleRef.current = null; // No single role is auto-checked
        } else {
          // For non-Admin title, clear all roles and check only the matching role
          let newRoles: { role_id: string }[] = [];
          if (matchingRole) {
            newRoles = [{ role_id: String(matchingRole.id) }];
          }
          setValue('role', newRoles);
          prevAutoCheckedRoleRef.current = matchingRole ? String(matchingRole.id) : null;
        }

        prevTitleRef.current = selectedTitle; // Update previous title
      }
    }, [selectedTitle, roleTitles, roles, setValue]);

    // In edit mode, check if all non-Admin roles are selected and auto-check Admin
    useEffect(() => {
      if (isEditMode) {
        const adminRole = roles.find((role) => role.name === 'Admin');
        const nonAdminRoles = roles.filter((role) => role.name !== 'Admin');
        const allNonAdminSelected = nonAdminRoles.every((role) =>
          selectedRoles.some((r) => r.role_id === String(role.id)),
        );

        if (
          adminRole &&
          allNonAdminSelected &&
          !selectedRoles.some((r) => r.role_id === String(adminRole.id))
        ) {
          setValue('role', [...selectedRoles, { role_id: String(adminRole.id) }]);
        }
      }
    }, [selectedRoles, roles, setValue, isEditMode]);

    useImperativeHandle(ref, () =>
      handleSubmit((data) => {
        const adminRole = roles.find((role) => role.name === 'Admin');
        const filteredRoles = adminRole
          ? data.role.filter((r) => r.role_id !== String(adminRole.id))
          : data.role;
        onSubmit({ ...data, role: filteredRoles });
      }),
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSubmit((data) => {
          const adminRole = roles.find((role) => role.name === 'Admin');
          const filteredRoles = adminRole
            ? data.role.filter((r) => r.role_id !== String(adminRole.id))
            : data.role;
          onSubmit({ ...data, role: filteredRoles });
        })();
      }
    };

    useEffect(() => {
      if (isSuccess) {
        reset();
        prevTitleRef.current = null; // Reset previous title
        prevAutoCheckedRoleRef.current = null; // Reset previous auto-checked role
      }
    }, [isSuccess, reset]);

    return (
      <Box
        as="form"
        onSubmit={handleSubmit((data) => {
          const adminRole = roles.find((role) => role.name === 'Admin');
          const filteredRoles = adminRole
            ? data.role.filter((r) => r.role_id !== String(adminRole.id))
            : data.role;
          onSubmit({ ...data, role: filteredRoles });
        })}
        fontFamily={'primary'}
        mb={2}
        py={3}
        onKeyDown={handleKeyDown}
      >
        <VStack gap={6} width="100%">
          <Controller
            name="first_name"
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label="First Name"
                placeholder="Enter first name"
                error={errors.first_name}
                required
              />
            )}
          />
          <Controller
            name="last_name"
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label="Last Name"
                placeholder="Enter last name"
                error={errors.last_name}
                required
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <CustomInput
                {...field}
                label="Email Address"
                placeholder="Enter email"
                error={errors.email}
                required
              />
            )}
          />

          <FormControl isInvalid={!!errors.title}>
            <FormLabel fontSize={'sm'} color={errors.title ? 'status.error' : 'text.secondary'}>
              Select Title
              <Asterisk />
            </FormLabel>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Select
                  rounded={'lg'}
                  {...field}
                  placeholder="Select Sub-Category"
                  bg={'background.white'}
                >
                  {roleTitles?.map((role: RoleTitle) => (
                    <option key={role.id} value={role.id} style={{ marginBottom: '2px' }}>
                      {role.name}
                    </option>
                  ))}
                </Select>
              )}
            />
            {errors.title && (
              <FormErrorMessage fontSize={'xs'}>{errors.title?.message}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.role}>
            <FormLabel fontSize={'sm'} color={errors.role ? 'status.error' : 'text.secondary'}>
              Select Roles
              <Asterisk />
            </FormLabel>
            <Controller
              name="role"
              control={control}
              render={({ field }) => {
                const adminRole = roles.find((role) => role.name === 'Admin');
                const isAdminChecked = adminRole
                  ? field.value.some((r) => r.role_id === String(adminRole.id))
                  : false;
                const selectedTitleName =
                  roleTitles?.find((title) => title.id.toString() === selectedTitle)?.name || '';
                const isAdminTitle = selectedTitleName.toLowerCase() === 'admin';

                return (
                  <SimpleGrid columns={2} spacing={4} width="100%">
                    {roles?.map((role: IRoles) => {
                      const isChecked = field.value.some((r) => r.role_id === String(role.id));
                      const isAdmin = role.name === 'Admin';
                      const isDisabled = (isAdminChecked || isAdminTitle) && !isAdmin;

                      return (
                        <Checkbox
                          key={role.id}
                          colorScheme="brand.primary"
                          size="lg"
                          borderColor={'#D0D5DD'}
                          isChecked={isChecked}
                          isDisabled={isDisabled}
                          onChange={(e) => {
                            if (isAdmin && e.target.checked) {
                              // If Admin is checked, select all roles
                              setValue(
                                'role',
                                roles.map((r) => ({ role_id: String(r.id) })),
                              );
                              prevAutoCheckedRoleRef.current = null; // Reset auto-checked role
                            } else if (isAdmin && !e.target.checked) {
                              // If Admin is unchecked, clear all roles
                              setValue('role', []);
                              prevAutoCheckedRoleRef.current = null; // Reset auto-checked role
                            } else if (!isDisabled) {
                              // Handle non-Admin role checkbox
                              if (e.target.checked) {
                                const newRoles = [...field.value, { role_id: String(role.id) }];
                                field.onChange(newRoles);
                              } else {
                                field.onChange(
                                  field.value.filter((r) => r.role_id !== String(role.id)),
                                );
                                // If the deselected role was auto-checked, clear the ref
                                if (prevAutoCheckedRoleRef.current === String(role.id)) {
                                  prevAutoCheckedRoleRef.current = null;
                                }
                              }
                            }
                          }}
                        >
                          <Text mt={1} color={'text.primary'} fontSize={'md'} ml={1}>
                            {role.name}
                          </Text>
                        </Checkbox>
                      );
                    })}
                  </SimpleGrid>
                );
              }}
            />
            {errors.role && (
              <FormErrorMessage fontSize={'xs'}>{errors.role.message}</FormErrorMessage>
            )}
          </FormControl>
        </VStack>
      </Box>
    );
  },
);

export default AddUserForm;
